import { Link } from "react-router";
import { ApiError, useActors, useMe, useRoutines } from "../api.ts";
import { Bento, Page, PageHeader, Pill, Tile } from "../calm.tsx";
import { canManageRoutines, formatInZone, formatLocal, RUN_STATUS_TONE, scheduleWords, sortRoutines, STATUS_LABEL, type RoutineListDTO } from "../lib/routines.ts";
import { useCurrentProject } from "../project.tsx";
import { Ago, ErrorNote } from "../ui.tsx";
import { money } from "./Run.tsx";

/*
 * Routines: scheduled agent processes and what they left behind (docs/specs/routines.md §3.1).
 * One row per routine; one that failed three times in a row sorts first because it needs a person.
 */

export function RoutinesPage() {
  // Scope to a project only when there is more than one to choose between, like Activity; otherwise an empty or failed project list would leave the query off.
  const { projectId, scoped, ready } = useCurrentProject();
  const routines = useRoutines(scoped ? projectId : undefined, ready);
  const actors = useActors().data;
  const me = useMe().data;
  const canManage = canManageRoutines(me);

  if (routines.error instanceof ApiError && routines.error.status === 404)
    return (
      <Page>
        <p className="text-ink-3">Routines are not available on this hub yet.</p>
      </Page>
    );
  if (routines.error)
    return (
      <Page>
        <ErrorNote error={routines.error} />
      </Page>
    );
  if (!ready || !routines.data)
    return (
      <Page>
        <p className="text-ink-3">Loading…</p>
      </Page>
    );

  const rows = sortRoutines(routines.data);
  const failing = rows.filter((r) => r.status === "paused_failing").length;

  return (
    <Page wide>
      <PageHeader
        title="Routines"
        meta={
          <span className="text-ink-3">
            {rows.length} routine{rows.length === 1 ? "" : "s"}
            {failing > 0 ? ` · ${failing} paused after repeated failures` : ""}
          </span>
        }
        actions={
          canManage ? (
            <Link to="/routines/new" data-testid="routines-new" className="rounded-full bg-ink px-4 py-2 text-[13.5px] text-paper hover:opacity-90">
              New routine
            </Link>
          ) : undefined
        }
      />

      {rows.length === 0 ? (
        <Bento className="mt-8">
          <Tile span={6} title="No routines yet" testId="routines-empty">
            <div className="space-y-3 text-[14px] leading-relaxed text-ink-2">
              <p>No routines yet. A routine is an agent that runs on a schedule and leaves a report here.</p>
              {canManage ? (
                <Link to="/routines/new" data-testid="routines-empty-new" className="inline-block">
                  <span className="inline-block rounded-full bg-ink px-4 py-2 text-[13.5px] text-paper">New routine</span>
                </Link>
              ) : (
                <p className="text-[13px] text-ink-3">An admin creates routines.</p>
              )}
            </div>
          </Tile>
        </Bento>
      ) : (
        <div className="mt-8 overflow-x-auto" data-testid="routines-list" tabIndex={0} role="region" aria-label="Routines">
          <table className="w-full min-w-[960px] border-collapse text-left text-[13.5px]">
            <thead>
              <tr className="border-b border-rule text-[12.5px] font-medium text-ink-3">
                <th className="py-2 pr-4 font-medium">Routine</th>
                <th className="py-2 pr-4 font-medium">Agent</th>
                <th className="py-2 pr-4 font-medium">Schedule</th>
                <th className="py-2 pr-4 font-medium">Next due</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">Last run</th>
                <th className="py-2 text-right font-medium">Cost, 30 days</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule/60">
              {rows.map((r) => (
                <RoutineRow key={r.id} routine={r} agentName={actors?.find((a) => a.id === r.agentId)?.name} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Page>
  );
}

function RoutineRow({ routine: r, agentName }: { routine: RoutineListDTO; agentName: string | undefined }) {
  const active = r.status === "active";
  return (
    <tr data-testid="routine-row" data-routine-id={r.id} data-routine-status={r.status} className="align-top">
      <td className="py-3 pr-4">
        <Link to={`/routines/${r.id}`} className="font-medium underline decoration-rule hover:text-ink" data-testid="routine-link">
          {r.name}
        </Link>
      </td>
      <td className="py-3 pr-4 text-ink-2">{agentName ?? r.agentId.slice(0, 10)}</td>
      <td className="py-3 pr-4 text-ink-2">{scheduleWords(r)}</td>
      <td className="py-3 pr-4 text-ink-2" data-testid="routine-next-due">
        {active ? (
          <>
            <span>{formatLocal(r.nextDueAt)}</span>
            <span className="block text-[12px] text-ink-3">
              {formatInZone(r.nextDueAt, r.timezone)} {r.timezone}
            </span>
          </>
        ) : (
          <span className="text-ink-3">not scheduled</span>
        )}
      </td>
      <td className="py-3 pr-4">
        <Pill tone={r.status === "active" ? "live" : r.status === "paused_failing" ? "danger" : "neutral"} testId="routine-status">
          {STATUS_LABEL[r.status]}
        </Pill>
        {r.status === "paused_failing" && <span className="mt-1 block text-[12px] text-ink-3">{r.failureStreak} failures in a row</span>}
      </td>
      <td className="py-3 pr-4 text-ink-2" data-testid="routine-last-run">
        {r.lastRun ? (
          <>
            <Pill tone={RUN_STATUS_TONE[r.lastRun.status] ?? "neutral"}>{r.lastRun.status.replaceAll("_", " ")}</Pill>
            <span className="mt-1 block text-[12px] text-ink-3">
              <Ago ts={r.lastRun.createdAt} /> · {money(r.lastRun.costUsd)}
            </span>
          </>
        ) : (
          <span className="text-ink-3">no runs yet</span>
        )}
      </td>
      <td className="py-3 text-right tabular-nums" data-testid="routine-cost-30d">
        {money(r.cost30dUsd)}
      </td>
    </tr>
  );
}
